import cx from 'classnames'
import { useLocation } from 'react-router'
import { MouseEvent, useEffect, useMemo } from 'react'

import usePageModal from 'features/Modal/hooks/usePageModal'
import { MODAL_MODE } from 'components/Modal/config'
import { Link } from 'features/i18n/router'
import { fromNowToFullDate } from 'lib/time'
import { Activity, ACTIVITY_PAGE_TYPE, CallType } from 'lib/_types'
import { snowplowEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { useLinger } from 'hooks'
import { useCurrentUser } from 'store/api/yay/user'

import Avatar from 'components/Avatar'
import { ActivityText } from './ActivityText'
import { ActivityUserList } from './ActivityUserList'
import { ExternalLink } from 'components/ExternalLink'

import './style.scss'

type Props = {
  activity: Activity
  onClick: (item: Activity) => (e: MouseEvent) => void
  pageType?: ACTIVITY_PAGE_TYPE
  enableViewportCheck?: boolean
  resizeTrigger?: number
}

export const ActivityListItem = ({
  activity,
  onClick,
  pageType,
  enableViewportCheck,
  resizeTrigger,
}: Props) => {
  const location = useLocation()
  const { data: currentUser } = useCurrentUser()
  const { generatePageModalUrl } = usePageModal()

  const { inViewRef } = useLinger({
    skip: !enableViewportCheck,
    enterCallback: (params) => {
      if (params.first_impression) {
        snowplowEvent.impression({
          notification: {
            type: pageType,
            user_id: activity.user?.id,
          },
        })
      }
    },
    leaveCallback: (params) => {
      snowplowEvent.linger({
        params,
        notification: {
          type: pageType,
          user_id: activity.user?.id,
        },
      })
    },
  })

  const link = useMemo(() => {
    switch (activity.type) {
      case 'repost':
      case 'conference_call_invite':
      case 'today_news':
      case 'popular_post':
      case 'tagged':
        return ROUTE_PATH.POST(activity.from_post?.id)

      case 'follow':
        return generatePageModalUrl(MODAL_MODE.USER_FOLLOWER, {
          userId: currentUser?.id,
        })

      case 'follow_request':
        return generatePageModalUrl(MODAL_MODE.FOLLOW_REQUEST)

      case 'follow_accepted':
      case 'profile_screenshot':
      case 'invitee_create_account':
      case 'follow_inviter':
      case 'followed_inviter':
        return ROUTE_PATH.USER(activity.user?.id)

      case 'user_birth_date':
        return generatePageModalUrl(MODAL_MODE.ACTIVITY_USERS_BIRTHDAY, {
          birthdate: activity.created_at,
        })

      case 'group_deputization_request':
      case 'group_deputization_accepted':
      case 'group_deputization_declined':
      case 'group_promote':
      case 'group_ownership':
      case 'group_invite':
      case 'group_created':
      case 'group_transfer_request':
      case 'group_transfer_accepted':
      case 'group_transfer_declined':
        return ROUTE_PATH.GROUP_DETAIL(activity.group?.id)

      case 'group_accepted':
        return ROUTE_PATH.GROUP(activity.group?.id)

      case 'group_request':
        return generatePageModalUrl(MODAL_MODE.GROUP_JOIN_REQUEST, {
          groupId: activity.group?.id,
        })

      case 'post_group_tagged_all':
        return ROUTE_PATH.GROUP_POST(activity.group?.id, activity.from_post?.id)

      case 'review_pending':
      case 'review_gift':
        return generatePageModalUrl(MODAL_MODE.USER_REVIEW_LIST, {
          userId: currentUser?.id,
        })

      case 'custom_announcement':
        return activity.metadata?.url || ''

      case 'gift_received_user':
      case 'gift_received_post':
      case 'gift_received_group':
      case 'gift_received_group_post':
        return ROUTE_PATH.USER_GIFTS(currentUser?.id)

      default:
        return location.pathname
    }
  }, [activity])

  const linkIcon = useMemo(() => {
    switch (activity.type) {
      case 'gift_received_user':
      case 'gift_received_post':
      case 'gift_received_group':
      case 'gift_received_group_post':
        return ROUTE_PATH.USER(activity.user?.id)

      default:
        return ''
    }
  }, [activity])

  const userList = useMemo(() => {
    switch (activity.type) {
      case 'follow':
        return activity.followers
      case 'like':
        return activity.to_post?.likers
      case 'user_birth_date':
        return activity.birthday_users ?? [activity.user]
      default:
        return
    }
  }, [activity])

  const withGroupIcon =
    activity['group']?.id && activity.type !== 'group_accepted'

  return (
    <div className="ActivitiesList__item" ref={inViewRef}>
      {link &&
        (activity?.type === 'custom_announcement' ? (
          <ExternalLink
            className="ActivitiesList__item__handle"
            href={link}
            onClick={onClick(activity)}
          />
        ) : (
          <Link
            className="ActivitiesList__item__handle"
            to={link}
            onClick={onClick(activity)}
          />
        ))}

      {(() => {
        const classNames = cx('ActivitiesList__item__a', {
          [`ActivitiesList__item__a--${activity.type}`]: true,
          'ActivitiesList__item__a--withGroupIcon': withGroupIcon,
          'ActivitiesList__item__a--conference_call_invite--withVideoIcon':
            activity.type === 'conference_call_invite' &&
            activity.from_post?.conference_call?.call_type === CallType.Video,
        })

        const icon = (withGroupIcon ||
          [
            'repost',
            'reply',
            'profile_screenshot',
            'follow_accepted',
            'follow_request',
            'conference_call_invite',
            'tagged',
            'review_pending',
            'review_gift',
            'popular_post',
            'invitee_create_account',
            'follow_inviter',
            'followed_inviter',
            'gift_received_user',
            'gift_received_post',
            'gift_received_group',
            'gift_received_group_post',
          ].includes(activity.type)) && <Avatar user={activity.user} />

        if (
          [
            'gift_received_user',
            'gift_received_post',
            'gift_received_group',
            'gift_received_group_post',
          ].includes(activity.type)
        ) {
          return (
            <Link
              className={cx(classNames, {
                'ActivitiesList__item__a--link': true,
              })}
              to={linkIcon}
            >
              {icon}
            </Link>
          )
        }

        return <div className={classNames}>{icon}</div>
      })()}

      <div className="ActivitiesList__item__center">
        {userList && (
          <ActivityUserList users={userList} resizeTrigger={resizeTrigger} />
        )}

        <div className="ActivitiesList__item__content">
          <ActivityText activity={activity} />
        </div>
      </div>

      <time className="ActivitiesList__item__time">
        {fromNowToFullDate(activity.created_at * 1000)}
      </time>
    </div>
  )
}
